// @ts-nocheck
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { TextField } from '@mui/material';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';

import Multiselect from '../../Multiselect';
import { getErrorHelperText, isValueEmpty } from '../../../../utils/Utils';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '../../../../const/CommonConst';
import useAttribute from '../../../../hooks/ModelInventory/useAttribute';
import {
  composeDraftBlurHandler,
  scheduleDraftPersistOnBlur,
} from '../../../../hooks/useDraftAutoSave.jsx';
import { DRAFT_INPUT_SX } from './TextFieldComponent';

const handleUserOptionEqualToValue = (option, val) =>
  String(option?.value) === String(val?.value);

const handleUserGetOptionLabel = (option) => option?.label ?? '';

const UserMultiSelectFieldComponent = ({
  value,
  onChange,
  usersList = EMPTY_ARRAY,
  label,
  titleLabel,
  attribute,
  entityDetailsFormikForm,
  isLoadingConstraints = false,
  required = false,
  formData = EMPTY_OBJECT,
  entityData = EMPTY_OBJECT,
  loader,
  hasDraftChip = false,
  onDraftBlur,
}) => {
  const { getUsersAttributesData } = useAttribute();
  const [apiUsersList, setApiUsersList] = useState(EMPTY_ARRAY);
  const [isLoading, setIsLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const skipConstraintLogic = false;

  const errorHelperText = useMemo(
    () =>
      getErrorHelperText(
        entityDetailsFormikForm?.errors?.[attribute?.id],
        entityDetailsFormikForm?.touched?.[attribute?.id]
      ),
    [
      entityDetailsFormikForm?.errors,
      entityDetailsFormikForm?.touched,
      attribute?.id,
    ]
  );

  const hasError = useMemo(() => Boolean(errorHelperText), [errorHelperText]);

  const filteredUsersList = useMemo(() => {
    // Use API users list when constraint logic is not skipped and API data is available
    const currentUsersList =
      !skipConstraintLogic && apiUsersList?.length > 0
        ? apiUsersList
        : usersList;

    // Filter valid users and remove duplicates based on username
    const uniqueUsernames = new Set();
    return currentUsersList
      .filter((user) => {
        if (user?.username && !uniqueUsernames.has(user.username)) {
          uniqueUsernames.add(user.username);
          return true;
        }
        return false;
      })
      .map((user) => ({
        label: user.username,
        value: user.user_id,
      }));
  }, [usersList, apiUsersList, skipConstraintLogic]);

  const fetchUsersData = useCallback(() => {
    if (isLoading || skipConstraintLogic || !entityData?.entity_type) {
      return;
    }
    setIsLoading(true);
    const payload = {
      source_attribute_id: Number.parseInt(attribute?.id),
      form_data: formData,
      ...(entityData?.draft_entity_key
        ? { draft_entity_key: entityData?.draft_entity_key }
        : { entity_id: entityData?.entity_id }),
      entity_type: entityData?.entity_type,
      skip_constraint_logic: skipConstraintLogic,
    };

    getUsersAttributesData(payload, loader)
      .then((res) => {
        if (res?.data) {
          setApiUsersList(res.data);
        } else {
          setApiUsersList(EMPTY_ARRAY);
        }
      })
      .catch(() => {
        setApiUsersList(EMPTY_ARRAY);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [
    attribute?.id,
    formData,
    getUsersAttributesData,
    isLoading,
    entityData,
    loader,
    skipConstraintLogic,
  ]);

  const handleDropdownOpen = useCallback(() => {
    setIsDropdownOpen(true);
    fetchUsersData();
  }, [fetchUsersData]);

  const handleDropdownClose = useCallback(() => {
    setIsDropdownOpen(false);
  }, [setIsDropdownOpen]);

  const selectedUsers = useMemo(() => {
    if (value == null || value === '') return [];
    const stored = Array.isArray(value) ? value : [value];
    return filteredUsersList.filter((user) =>
      stored.some((v) => String(v) === String(user.value) || v === user.label)
    );
  }, [value, filteredUsersList]);

  const handleChange = useCallback(
    (newSelectedUsers) => {
      if (isValueEmpty(newSelectedUsers)) {
        onChange(null);
      } else {
        onChange(newSelectedUsers?.map((user) => user?.value));
      }
      // Chip X removal doesn't fire a blur event, so we schedule draft manually.
      // For dropdown selections, handleInputBlur will handle it on blur.
      if (!isDropdownOpen) {
        scheduleDraftPersistOnBlur(onDraftBlur);
      }
    },
    [onChange, onDraftBlur, isDropdownOpen]
  );

  const handleInputBlur = useCallback(
    (event) => {
      composeDraftBlurHandler(
        entityDetailsFormikForm?.handleBlur,
        onDraftBlur
      )(event);
    },
    [entityDetailsFormikForm?.handleBlur, onDraftBlur]
  );

  const renderUserSelectTextField = useCallback(
    (params) => (
      <TextField
        {...params}
        name={attribute?.id}
        label={label}
        title={titleLabel}
        required={required}
        error={hasError}
        helperText={errorHelperText}
        sx={hasDraftChip ? DRAFT_INPUT_SX : undefined}
        slotProps={{
          ...params?.slotProps,
          input: {
            ...params?.slotProps?.input,
            placeholder: selectedUsers?.length > 0 ? '' : 'Select user',
          },

          inputLabel: {
            ...params?.slotProps?.inputLabel,
            shrink: true,
          },
        }}
      />
    ),
    [
      label,
      titleLabel,
      required,
      hasError,
      errorHelperText,
      hasDraftChip,
      attribute?.id,
      selectedUsers,
    ]
  );

  // Re-fetch users and validate selection when constraints finish loading, this is added because when constraints are applied, the available options might change based on the constraint logic and we need to make sure that the selected value is still valid with the new options, if not we need to clear the selection.
  const prevIsLoadingConstraintsRef = useRef(isLoadingConstraints);
  useEffect(() => {
    const wasLoading = prevIsLoadingConstraintsRef.current;
    prevIsLoadingConstraintsRef.current = isLoadingConstraints;

    // When constraints transition from loading → done, re-fetch users
    if (
      wasLoading &&
      !isLoadingConstraints &&
      !skipConstraintLogic &&
      attribute?.id
    ) {
      const payload = {
        source_attribute_id: Number.parseInt(attribute?.id),
        form_data: formData,
        ...(entityData?.draft_entity_key
          ? { draft_entity_key: entityData?.draft_entity_key }
          : { entity_id: entityData?.entity_id }),
        entity_type: entityData?.entity_type,
        skip_constraint_logic: skipConstraintLogic,
      };

      getUsersAttributesData(payload, false)
        .then((res) => {
          if (res?.data) {
            setApiUsersList(res.data);
            // Remove invalid selections from current value
            if (Array.isArray(value) && value.length > 0) {
              const validUserIds = new Set(
                res.data.map((user) => String(user?.user_id))
              );
              const validValues = value.filter(
                (v) =>
                  validUserIds.has(String(v)) ||
                  res.data.some((user) => user?.username === v)
              );
              if (validValues.length !== value.length) {
                onChange(validValues.length > 0 ? validValues : null);
              }
            }
          } else {
            setApiUsersList(EMPTY_ARRAY);
            if (Array.isArray(value) && value.length > 0) {
              onChange(null);
            }
          }
        })
        .catch(() => {});
    }
  }, [isLoadingConstraints]);

  return (
    <Multiselect
      options={isLoading ? EMPTY_ARRAY : filteredUsersList}
      value={isEmpty(selectedUsers) ? [] : selectedUsers}
      getOptionLabel={handleUserGetOptionLabel}
      isOptionEqualToValue={handleUserOptionEqualToValue}
      renderInput={renderUserSelectTextField}
      onChange={handleChange}
      onBlur={handleInputBlur}
      onOpen={handleDropdownOpen}
      onClose={handleDropdownClose}
      open={isDropdownOpen}
      loading={isLoading}
      loadingText="Loading users..."
      disabled={isLoadingConstraints}
      disableSelectAll
      noOptionsMessage="No users present."
    />
  );
};

UserMultiSelectFieldComponent.propTypes = {
  label: PropTypes.string.isRequired,
  titleLabel: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChange: PropTypes.func.isRequired,
  // Add prop types validation
  attribute: PropTypes.shape({
    id: PropTypes.number,
    description: PropTypes.string,
  }).isRequired,
  entityDetailsFormikForm: PropTypes.shape({
    errors: PropTypes.objectOf(PropTypes.string),
    touched: PropTypes.objectOf(PropTypes.bool),
    setFieldTouched: PropTypes.func.isRequired,
    handleBlur: PropTypes.func,
  }),
  usersList: PropTypes.arrayOf(
    PropTypes.shape({
      username: PropTypes.string,
      user_id: PropTypes.number,
    })
  ),
  isLoadingConstraints: PropTypes.bool,
  required: PropTypes.bool,
  username: PropTypes.string.isRequired,
  user_id: PropTypes.number.isRequired,
  formData: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.bool,
      PropTypes.array,
      PropTypes.object,
    ])
  ),
  entityData: PropTypes.objectOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.number,
      PropTypes.bool,
      PropTypes.array,
      PropTypes.object,
    ])
  ),
  loader: PropTypes.bool,
  hasDraftChip: PropTypes.bool,
  onDraftBlur: PropTypes.func,
};

export default UserMultiSelectFieldComponent;
